import { useState, useRef } from "react";
import { uploadFile as uploadFileToStorage } from "@/lib/upload";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListAttachments,
  useCreateAttachment,
  useDeleteAttachment,
  getListAttachmentsQueryKey,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Paperclip, FileText, ImageIcon, Loader2, X, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

interface Attachment {
  id: number;
  estimateId: number;
  fileName: string;
  contentType: string;
  objectPath: string;
  createdAt: string;
}

const ALLOWED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
  "application/pdf",
];

function isImage(contentType: string) {
  return contentType.startsWith("image/");
}

function isPdf(contentType: string) {
  return contentType === "application/pdf";
}


function AttachmentCard({
  attachment,
  onView,
  onDelete,
}: {
  attachment: Attachment;
  onView: (a: Attachment) => void;
  onDelete: (a: Attachment) => void;
}) {
  const isImg = isImage(attachment.contentType);

  return (
    <div
      className={cn(
        "group relative flex flex-col gap-2 rounded-lg border border-border/60 bg-card p-3 cursor-pointer",
        "hover:border-primary/40 hover:bg-muted/30 transition-colors w-[120px] shrink-0"
      )}
      onClick={() => onView(attachment)}
      title={attachment.fileName}
    >
      <div className="flex items-center justify-center h-12 w-full rounded bg-muted/40">
        {isImg ? (
          <ImageIcon className="w-6 h-6 text-blue-400/70" />
        ) : (
          <FileText className="w-6 h-6 text-red-400/70" />
        )}
      </div>
      <p className="text-xs font-medium text-foreground truncate max-w-[104px]">
        {attachment.fileName}
      </p>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">
        {isImg ? "Image" : isPdf(attachment.contentType) ? "PDF" : "File"}
      </p>
      <button
        className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-destructive/10"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(attachment);
        }}
        title="Remove attachment"
      >
        <X className="w-3 h-3 text-destructive" />
      </button>
    </div>
  );
}

function AttachmentViewer({
  attachment,
  open,
  onClose,
}: {
  attachment: Attachment | null;
  open: boolean;
  onClose: () => void;
}) {
  if (!attachment) return null;

  const objectUrl = `/api/storage${attachment.objectPath}`;
  const isImg = isImage(attachment.contentType);
  const isPDF = isPdf(attachment.contentType);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl w-full h-[80vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-4 py-3 border-b">
          <DialogTitle className="text-base font-medium truncate flex items-center gap-2">
            {isImg ? (
              <ImageIcon className="w-4 h-4 text-muted-foreground shrink-0" />
            ) : (
              <FileText className="w-4 h-4 text-red-500 shrink-0" />
            )}
            {attachment.fileName}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-hidden">
          {isImg ? (
            <div className="w-full h-full flex items-center justify-center bg-black/5 p-4">
              <img
                src={objectUrl}
                alt={attachment.fileName}
                className="max-w-full max-h-full object-contain rounded"
              />
            </div>
          ) : isPDF ? (
            <iframe
              src={objectUrl}
              title={attachment.fileName}
              className="w-full h-full border-0"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
              Preview not available for this file type.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function AttachmentsPanel({ estimateId }: { estimateId: number }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: attachments = [], isLoading } = useListAttachments(estimateId, {
    query: { queryKey: getListAttachmentsQueryKey(estimateId) },
  });

  const createAttachment = useCreateAttachment();
  const deleteAttachment = useDeleteAttachment();

  const [viewingAttachment, setViewingAttachment] = useState<Attachment | null>(null);
  const [deletingAttachment, setDeletingAttachment] = useState<Attachment | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFilesSelected = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter((f) => ALLOWED_TYPES.includes(f.type));
    if (validFiles.length === 0) {
      toast({ title: "Only JPEG, PNG, GIF, WebP, or PDF files are supported", variant: "destructive" });
      return;
    }
    if (validFiles.length < files.length) {
      toast({ title: `${files.length - validFiles.length} unsupported file(s) skipped` });
    }

    setIsUploading(true);
    let successCount = 0;

    for (const file of validFiles) {
      try {
        const { objectPath } = await uploadFileToStorage(file);
        await createAttachment.mutateAsync({
          estimateId,
          data: {
            fileName: file.name,
            contentType: file.type,
            objectPath,
          },
        });
        successCount++;
      } catch (err) {
        toast({ title: `Failed to upload "${file.name}"`, variant: "destructive" });
      }
    }

    if (successCount > 0) {
      await queryClient.invalidateQueries({ queryKey: getListAttachmentsQueryKey(estimateId) });
      toast({ title: `${successCount} file${successCount > 1 ? "s" : ""} attached` });
    }

    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDelete = async () => {
    if (!deletingAttachment) return;
    try {
      await deleteAttachment.mutateAsync({
        estimateId,
        attachmentId: deletingAttachment.id,
      });
      await queryClient.invalidateQueries({ queryKey: getListAttachmentsQueryKey(estimateId) });
      toast({ title: "Attachment removed" });
    } catch {
      toast({ title: "Failed to remove attachment", variant: "destructive" });
    } finally {
      setDeletingAttachment(null);
    }
  };

  return (
    <Card className="shadow-sm border-border/50">
      <CardHeader className="flex flex-row items-center justify-between pb-3 border-b bg-muted/40">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-muted-foreground" />
            Attachments
          </CardTitle>
          <CardDescription className="text-xs mt-0.5">
            Editor-only — images and PDFs not included in exported PDFs.
          </CardDescription>
        </div>
        <div>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_TYPES.join(",")}
            className="sr-only"
            onChange={(e) => handleFilesSelected(e.target.files)}
          />
          <Button
            size="sm"
            variant="secondary"
            disabled={isUploading}
            onClick={() => fileInputRef.current?.click()}
            className="text-xs"
          >
            {isUploading ? (
              <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />Uploading…</>
            ) : (
              <><Plus className="w-3.5 h-3.5 mr-1.5" />Add Files</>
            )}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-4">
        {isLoading ? (
          <p className="text-sm text-muted-foreground text-center py-4">Loading…</p>
        ) : attachments.length === 0 ? (
          <div
            className="border-2 border-dashed border-border/50 rounded-lg py-8 text-center cursor-pointer hover:border-primary/30 hover:bg-muted/20 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <Paperclip className="w-6 h-6 text-muted-foreground/40 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              Click to upload images or PDFs
            </p>
            <p className="text-xs text-muted-foreground/60 mt-1">JPEG, PNG, GIF, WebP, PDF</p>
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {attachments.map((a) => (
              <AttachmentCard
                key={a.id}
                attachment={a as Attachment}
                onView={setViewingAttachment}
                onDelete={setDeletingAttachment}
              />
            ))}
          </div>
        )}
      </CardContent>

      <AttachmentViewer
        attachment={viewingAttachment}
        open={!!viewingAttachment}
        onClose={() => setViewingAttachment(null)}
      />

      <AlertDialog open={!!deletingAttachment} onOpenChange={(v) => !v && setDeletingAttachment(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Attachment?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deletingAttachment?.fileName}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDelete(); }}
              className="bg-destructive text-destructive-foreground"
            >
              {deleteAttachment.isPending && (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              )}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
